import { CSSProperties, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useAuthStore } from '../auth/useAuthStore';
import { ContributionsAdminPage } from './admin/ContributionsAdminPage';
import { TechnicalAccessModal } from './components/TechnicalAccessModal';

// ── Institutional header ──────────────────────────────────────────────────────

function ResultsHeader({ slug }: { slug: string }): JSX.Element {
  const { logout } = useAuthStore();

  const s: CSSProperties = {
    background: '#fff',
    borderBottom: '1px solid #ececec',
    padding: '12px 28px',
    display: 'flex',
    alignItems: 'center',
    gap: 14,
    position: 'sticky',
    top: 0,
    zIndex: 500,
  };

  return (
    <header style={s}>
      <div style={{ width: 34, height: 34, borderRadius: 9, background: '#1a1a2e', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          <path d="M9 22V12h6v10" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      </div>

      <div>
        <div style={{ fontSize: 11, fontWeight: 700, color: '#94a3b8', letterSpacing: '0.05em', lineHeight: 1 }}>
          Consulta Ciudadana
        </div>
        <div style={{ fontSize: 14.5, fontWeight: 800, color: '#1a1a2e', lineHeight: 1.2 }}>
          San Cristóbal de las Casas
        </div>
      </div>

      <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 10 }}>
        <Link
          to={`/p/${slug}/participa`}
          style={{
            padding: '7px 14px', borderRadius: 20,
            border: '1.5px solid #e0e0e0', background: '#fff',
            color: '#374151', fontSize: 12.5, fontWeight: 600,
            textDecoration: 'none', whiteSpace: 'nowrap',
          }}
        >
          ← Volver a la consulta
        </Link>
        <button
          type="button"
          onClick={() => { logout(); }}
          style={{
            padding: '7px 14px', borderRadius: 20,
            border: '1.5px solid #e0e0e0', background: '#fff',
            color: '#9ca3af', fontSize: 12, fontWeight: 600,
            cursor: 'pointer', whiteSpace: 'nowrap',
          }}
        >
          Cerrar sesión
        </button>
      </div>
    </header>
  );
}

// ── Access denied — not redirecting to Wayline login ──────────────────────────

function AccessGate({ slug }: { slug: string }): JSX.Element {
  const [showModal, setShowModal] = useState(false);

  return (
    <div style={{ minHeight: '100vh', background: '#f5f7fa', display: 'flex', flexDirection: 'column', fontFamily: "'Segoe UI', system-ui, sans-serif" }}>
      {/* Minimal institutional header */}
      <header style={{ background: '#fff', borderBottom: '1px solid #ececec', padding: '12px 28px', display: 'flex', alignItems: 'center', gap: 12 }}>
        <div style={{ width: 34, height: 34, borderRadius: 9, background: '#1a1a2e', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
            <path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            <path d="M9 22V12h6v10" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </div>
        <div>
          <div style={{ fontSize: 11, fontWeight: 700, color: '#94a3b8', letterSpacing: '0.05em' }}>Consulta Ciudadana</div>
          <div style={{ fontSize: 14.5, fontWeight: 800, color: '#1a1a2e' }}>San Cristóbal de las Casas</div>
        </div>
      </header>

      <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
        <div style={{ background: '#fff', borderRadius: 18, padding: '48px 44px', maxWidth: 440, width: '100%', textAlign: 'center', border: '1.5px solid #f0f0f0', boxShadow: '0 4px 24px rgba(0,0,0,0.07)' }}>
          <div style={{ width: 52, height: 52, borderRadius: 14, background: '#f8fafc', border: '1.5px solid #e8e8e8', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 22px' }}>
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden="true">
              <rect x="3" y="11" width="18" height="11" rx="2" stroke="#94a3b8" strokeWidth="2"/>
              <path d="M7 11V7a5 5 0 0 1 10 0v4" stroke="#94a3b8" strokeWidth="2" strokeLinecap="round"/>
            </svg>
          </div>
          <h2 style={{ margin: '0 0 12px', fontSize: 20, fontWeight: 800, color: '#1a1a2e' }}>
            Sección del equipo técnico
          </h2>
          <p style={{ margin: '0 0 28px', fontSize: 14, color: '#6b7280', lineHeight: 1.65 }}>
            Esta área está destinada al equipo responsable del análisis de la consulta ciudadana de San Cristóbal de las Casas.
          </p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <button
              type="button"
              onClick={() => setShowModal(true)}
              style={{ width: '100%', padding: '12px', borderRadius: 10, border: 'none', background: '#1a1a2e', color: '#fff', fontSize: 14, fontWeight: 700, cursor: 'pointer' }}
            >
              Ingresar
            </button>
            <Link
              to={`/p/${slug}/participa`}
              style={{ display: 'block', width: '100%', padding: '11px', borderRadius: 10, border: '1.5px solid #e0e0e0', background: '#fff', color: '#6b7280', fontSize: 13.5, fontWeight: 600, textDecoration: 'none', textAlign: 'center', boxSizing: 'border-box' }}
            >
              Volver a la consulta ciudadana
            </Link>
          </div>
        </div>
      </div>

      {showModal && (
        <TechnicalAccessModal slug={slug} onClose={() => setShowModal(false)} />
      )}
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────

export function ResultsPage(): JSX.Element {
  const { projectSlug } = useParams<{ projectSlug: string }>();
  const slug = projectSlug ?? 'sclc';

  const { isAuthenticated } = useAuthStore();

  if (!isAuthenticated) {
    return <AccessGate slug={slug} />;
  }

  return (
    <div style={{ minHeight: '100vh', background: '#f5f7fa', fontFamily: "'Segoe UI', system-ui, sans-serif" }}>
      <ResultsHeader slug={slug} />
      <ContributionsAdminPage hideInternalHeader />
    </div>
  );
}
